import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { ImpressoraDTO } from '../../../models/impressora.models';
import { ImpressoraService } from '../../../services/impressora/impressora.service';

/**
 * Dialogo de cadastro rapido de impressora, espelhando `MarcaAdicionarDialogComponent`.
 *
 * Existe para que o cadastro inline saia de dentro da tela de configuracao da empresa: la ele
 * estava amarrado ao campo de VENDA (`salvarImpressora` atribuia sempre
 * `form.impressoraPadraoId`), entao o mesmo dialogo nao servia ao campo de PEDIDO. Aqui o
 * dialogo so CRIA e emite — quem decide onde guardar e o `app-select-impressora`.
 *
 * Usa as chaves de i18n que ja existiam na raiz (`Nome_da_Impressora`, `Caminho_da_Impressora`,
 * ...), entao nao foi preciso criar traducao nova.
 */
@Component({
  selector: 'app-impressora-adicionar-dialog',
  templateUrl: './impressora-adicionar-dialog.component.html',
  styleUrls: ['./impressora-adicionar-dialog.component.scss'],
})
export class ImpressoraAdicionarDialogComponent implements OnChanges {

  @Input() visible = false;
  /**
   * Quando preenchido, o dialog entra em modo EDICAO: pre-carrega os campos e usa `PUT` em vez
   * de `POST`. Null/undefined mantem o comportamento de cadastro rapido.
   */
  @Input() impressora: ImpressoraDTO | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emitido tanto na criacao quanto na edicao — o pai decide o que fazer com o registro. */
  @Output() impressoraCriada = new EventEmitter<ImpressoraDTO>();

  codigoImpressora: number | null = null;
  nome = '';
  caminho = '';
  salvando = false;

  get ehEdicao(): boolean {
    return !!this.impressora?.id;
  }

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private impressoraService: ImpressoraService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetar();
    }
  }

  /** Chave i18n do titulo/subtitulo, conforme o modo. */
  get chaveTitulo(): string {
    return this.ehEdicao ? 'Editar_Impressora' : 'Adicionar_Impressora';
  }

  get chaveSubtitulo(): string {
    return this.ehEdicao ? 'Altere_os_dados_da_impressora' : 'Preencha_os_dados_da_nova_impressora';
  }

  submit(): void {
    if (!this.nome.trim() || !this.caminho.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('configuracao-empresa.mensagens.preencha_todos_campos'),
      });
      return;
    }

    this.salvando = true;

    // `codigoImpressora` e opcional para o usuario. O fallback existe so para satisfazer o DTO:
    // verificado em runtime que o backend atribui o codigo sequencial por empresa e ignora o
    // valor enviado. NAO confundir com o `id` (PK) — o vinculo do item e da empresa usa o `id`.
    const body: ImpressoraDTO = {
      codigoImpressora: this.codigoImpressora || Date.now(),
      nome: this.nome.trim(),
      caminho: this.caminho.trim(),
    };

    // `put` e `post` do service declaram tipos de retorno diferentes; unificamos em
    // `Observable<any>` porque aqui so interessa o registro salvo, que ambos devolvem.
    const requisicao: Observable<any> = this.ehEdicao
      ? this.impressoraService.put(`/impressora/${this.impressora!.id}`, { ...this.impressora, ...body } as any)
      : this.impressoraService.post('/impressora', body);

    requisicao.subscribe({
      next: (salva: any) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant(
            this.ehEdicao
              ? 'impressoras.mensagens.impressora_atualizada'
              : 'configuracao-empresa.mensagens.impressora_adicionada'
          ),
        });
        this.impressoraCriada.emit(salva);
        this.fechar();
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('configuracao-empresa.mensagens.erro_salvar_impressora'),
        });
      },
    });
  }

  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private resetar(): void {
    // Em modo edicao, "resetar" significa recarregar os dados do registro alvo.
    if (this.ehEdicao) {
      this.codigoImpressora = this.impressora!.codigoImpressora ?? null;
      this.nome = this.impressora!.nome ?? '';
      this.caminho = this.impressora!.caminho ?? '';
      this.salvando = false;
      return;
    }
    this.codigoImpressora = null;
    this.nome = '';
    this.caminho = '';
    this.salvando = false;
  }
}
